'use client'

import LocalStorageManager from '@/lib/utils/localStorageManager'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

interface GraphicHistoryItem {
  graphicId: string
  previewUrl: string
}

interface GraphicHistoryData {
  history: GraphicHistoryItem[]
  currentGraphicId: string | null
}

interface GraphicHistoryContextType {
  getGraphicData: (initialGraphicId: string) => GraphicHistoryData
  addGraphic: (initialGraphicId: string, graphicId: string, previewUrl: string) => void
  setCurrentGraphicId: (initialGraphicId: string, currentGraphicId: string) => void
  getCurrentEditingInitialId: () => string | null
  setCurrentEditingInitialId: (initialId: string | null) => void
  clearHistory: (initialGraphicId?: string) => void
}

const GraphicHistoryContext = createContext<GraphicHistoryContextType | undefined>(undefined)

interface GraphicHistoryProviderProps {
  children: ReactNode
}

const STORAGE_KEY_PREFIX = 'sprites-graphic-history-'

/**
 * GraphicHistoryProvider manages a history of graphics with their history and current state.
 * It uses localStorage to persist data across sessions.
 * Each initialGraphicId acts as a key to store its own history data.
 */
export function GraphicHistoryProvider({ children }: GraphicHistoryProviderProps) {
  const [historyMap, setHistoryMap] = useState<Map<string, GraphicHistoryData>>(new Map())
  const [currentEditingInitialId, setCurrentEditingInitialIdState] = useState<string | null>(null)

  // Load history from localStorage on mount
  useEffect(() => {
    const loadHistory = () => {
      const newHistoryMap = new Map<string, GraphicHistoryData>()

      // Iterate through localStorage to find all graphic history entries
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith(STORAGE_KEY_PREFIX)) {
          const initialGraphicId = key.replace(STORAGE_KEY_PREFIX, '')
          try {
            const historyData = JSON.parse(LocalStorageManager.getItem(key) || '{}')
            if (historyData.history && Array.isArray(historyData.history)) {
              newHistoryMap.set(initialGraphicId, {
                history: historyData.history,
                currentGraphicId: historyData.currentGraphicId || null,
              })
            }
          } catch (error) {
            console.warn(`Failed to parse history data for ${initialGraphicId}:`, error)
            // Remove corrupted data
            LocalStorageManager.removeItem(key)
          }
        }
      }

      setHistoryMap(newHistoryMap)
    }

    loadHistory()
  }, [])

  // Save history to localStorage whenever it changes
  const saveToStorage = (initialGraphicId: string, data: GraphicHistoryData) => {
    try {
      LocalStorageManager.setItem(STORAGE_KEY_PREFIX + initialGraphicId, JSON.stringify(data))
    } catch (error) {
      console.warn(`Failed to save history data for ${initialGraphicId}:`, error)
    }
  }

  // Remove from localStorage
  const removeFromStorage = (initialGraphicId: string) => {
    LocalStorageManager.removeItem(STORAGE_KEY_PREFIX + initialGraphicId)
  }

  const getGraphicData = (initialGraphicId: string): GraphicHistoryData => {
    const historyData = historyMap.get(initialGraphicId)
    return historyData || { history: [], currentGraphicId: null }
  }

  const addGraphic = (initialGraphicId: string, graphicId: string, previewUrl: string): void => {
    setHistoryMap((prevMap) => {
      const newMap = new Map(prevMap)
      const existingData = newMap.get(initialGraphicId) || { history: [], currentGraphicId: null }

      // Check if this graphic already exists in history
      const existingIndex = existingData.history.findIndex((item) => item.graphicId === graphicId)

      if (existingIndex === -1) {
        // Add new graphic to history
        const newHistory = [...existingData.history, { graphicId, previewUrl }]
        const updatedData = {
          ...existingData,
          history: newHistory,
          currentGraphicId: graphicId, // Set as current when adding
        }

        newMap.set(initialGraphicId, updatedData)
        saveToStorage(initialGraphicId, updatedData)
      } else {
        // Update existing graphic's preview URL
        const newHistory = [...existingData.history]
        newHistory[existingIndex] = { graphicId, previewUrl }
        const updatedData = {
          ...existingData,
          history: newHistory,
        }

        newMap.set(initialGraphicId, updatedData)
        saveToStorage(initialGraphicId, updatedData)
      }

      return newMap
    })
  }

  const setCurrentGraphicId = (initialGraphicId: string, currentGraphicId: string): void => {
    setHistoryMap((prevMap) => {
      const newMap = new Map(prevMap)
      const existingData = newMap.get(initialGraphicId) || { history: [], currentGraphicId: null }

      // Verify that the currentGraphicId exists in history
      const exists = existingData.history.some((item) => item.graphicId === currentGraphicId)

      if (exists) {
        const updatedData = {
          ...existingData,
          currentGraphicId,
        }

        newMap.set(initialGraphicId, updatedData)
        saveToStorage(initialGraphicId, updatedData)
      } else {
        console.warn(`Graphic ID ${currentGraphicId} not found in history for ${initialGraphicId}`)
      }

      return newMap
    })
  }

  const getCurrentEditingInitialId = (): string | null => {
    return currentEditingInitialId
  }

  const setCurrentEditingInitialId = (initialId: string | null): void => {
    setCurrentEditingInitialIdState(initialId)
  }

  const clearHistory = (initialGraphicId?: string): void => {
    if (initialGraphicId) {
      // Clear history for specific initialGraphicId
      setHistoryMap((prevMap) => {
        const newMap = new Map(prevMap)
        newMap.delete(initialGraphicId)
        removeFromStorage(initialGraphicId)
        return newMap
      })
    } else {
      // Clear all history
      setHistoryMap((prevMap) => {
        // Remove all items from localStorage
        prevMap.forEach((_, key) => {
          removeFromStorage(key)
        })
        return new Map()
      })
    }
  }

  const contextValue: GraphicHistoryContextType = {
    getGraphicData,
    addGraphic,
    setCurrentGraphicId,
    getCurrentEditingInitialId,
    setCurrentEditingInitialId,
    clearHistory,
  }

  return (
    <GraphicHistoryContext.Provider value={contextValue}>{children}</GraphicHistoryContext.Provider>
  )
}

export function useGraphicHistory() {
  const context = useContext(GraphicHistoryContext)
  if (context === undefined) {
    throw new Error('useGraphicHistory must be used within a GraphicHistoryProvider')
  }
  return context
}

export type { GraphicHistoryContextType, GraphicHistoryData, GraphicHistoryItem }
